import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type Theme = 'light' | 'dark' | 'system'
export type FontSize = 'sm' | 'md' | 'lg'

interface UIState {
  theme: Theme
  resolvedTheme: 'light' | 'dark'
  fontSize: FontSize
  sidebarCollapsed: boolean
  mobileSidebarOpen: boolean
  setTheme: (theme: Theme) => void
  setFontSize: (size: FontSize) => void
  toggleSidebar: () => void
  setMobileSidebarOpen: (open: boolean) => void
}

function resolveTheme(theme: Theme): 'light' | 'dark' {
  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return theme
}

function applyTheme(resolved: 'light' | 'dark') {
  document.documentElement.classList.remove('light', 'dark')
  document.documentElement.classList.add(resolved)
}

function applyFontSize(size: FontSize) {
  document.documentElement.classList.remove('font-sm', 'font-md', 'font-lg')
  document.documentElement.classList.add(`font-${size}`)
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      theme: 'light',
      resolvedTheme: 'light',
      fontSize: 'md',
      sidebarCollapsed: false,
      mobileSidebarOpen: false,
      setTheme: (theme: Theme) => {
        const resolved = resolveTheme(theme)
        set({ theme, resolvedTheme: resolved })
        applyTheme(resolved)
      },
      setFontSize: (size: FontSize) => {
        set({ fontSize: size })
        applyFontSize(size)
      },
      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      setMobileSidebarOpen: (open: boolean) => set({ mobileSidebarOpen: open }),
    }),
    {
      name: 'xident-admin-ui',
      partialize: (state) => ({ theme: state.theme, fontSize: state.fontSize, sidebarCollapsed: state.sidebarCollapsed }),
    }
  )
)

// Initialize theme + font size on load
if (typeof window !== 'undefined') {
  try {
    const stored = localStorage.getItem('xident-admin-ui')
    const state = stored ? JSON.parse(stored).state : {}
    const theme: Theme = state?.theme ?? 'light'
    const fontSize: FontSize = state?.fontSize ?? 'md'
    applyTheme(resolveTheme(theme))
    applyFontSize(fontSize)
  } catch {
    applyTheme('light')
  }
}
